import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Contact } from '@/types'
import { Link } from 'react-router-dom'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ClickToCallButton } from '@/components/ClickToCallButton'
import { InactiveBadge } from '@/components/InactiveBadge'
import { cn, formatDate } from '@/lib/utils'

interface ContactsColumnsProps {
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
}

export const createContactsColumns = ({
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
}: ContactsColumnsProps = {}): ColumnDef<Contact>[] => [
  {
    id: 'select',
    header: () => (
      <Checkbox
        checked={allSelected}
        onCheckedChange={onToggleSelectAll}
        aria-label="Select all contacts"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selectedIds?.has(row.original.id) ?? false}
        onCheckedChange={() => onToggleSelect?.(row.original.id)}
        aria-label={`Select contact ${row.original.name}`}
      />
    ),
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
  },
  {
    id: 'avatar',
    header: '',
    size: 60,
    minSize: 50,
    maxSize: 80,
    enableResizing: false,
    cell: ({ row }) => {
      const firstName = row.original.first_name || ''
      const lastName = row.original.last_name || ''
      const initials = (firstName[0] || '') + (lastName[0] || '') || '??'

      return (
        <Avatar className="h-8 w-8">
          <AvatarFallback>{initials.toUpperCase()}</AvatarFallback>
        </Avatar>
      )
    },
  },
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    meta: { sortField: 'name' },
    size: 200,
    cell: ({ row }) => {
      const isInactive = row.original.status__c === 'Inactive'
      return (
        <div className="flex items-center gap-2">
          <Link
            to={`/contacts/${row.original.id}`}
            className={cn(
              'font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 truncate',
              isInactive && 'line-through text-muted-foreground'
            )}
            aria-label={`View details for contact ${row.original.name}${isInactive ? ' (inactive)' : ''}`}
          >
            {row.original.name || '-'}
          </Link>
          {isInactive && <InactiveBadge />}
        </div>
      )
    },
  },
  {
    id: 'title',
    header: 'Title',
    accessorKey: 'title',
    meta: { sortField: 'title' },
    size: 180,
  },
  {
    id: 'email',
    header: 'Email',
    accessorKey: 'email',
    meta: { sortField: 'email' },
    size: 220,
    cell: ({ row }) =>
      row.original.email ? (
        <a
          href={`mailto:${row.original.email}`}
          className="text-primary hover:underline"
          aria-label={`Email ${row.original.first_name} ${row.original.last_name}`}
        >
          {row.original.email}
        </a>
      ) : null,
  },
  {
    id: 'phone',
    header: 'Phone',
    accessorKey: 'phone',
    size: 180,
    cell: ({ row }) =>
      row.original.phone ? (
        <ClickToCallButton
          phoneNumber={row.original.phone}
          whoId={row.original.id}
          whoType="Contact"
          whoName={`${row.original.first_name} ${row.original.last_name}`}
          whatId={row.original.account_id}
          whatType="Account"
        />
      ) : null,
  },
  {
    id: 'account_name',
    header: 'Account',
    accessorKey: 'account_name',
    meta: { sortField: 'account_name' },
    size: 180,
    cell: ({ row }) =>
      row.original.account_id && row.original.account_name ? (
        <Link
          to={`/accounts/${row.original.account_id}`}
          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`View account: ${row.original.account_name}`}
        >
          {row.original.account_name}
        </Link>
      ) : (
        <span>{row.original.account_name || '-'}</span>
      ),
  },
  {
    id: 'owner_name',
    header: 'Owner',
    accessorKey: 'owner_name',
    meta: { sortField: 'owner_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">
        {(row.original as any).owner_name || '-'}
      </span>
    ),
  },
  {
    id: 'marketing_status__c',
    header: 'Marketing Status',
    accessorKey: 'marketing_status__c',
    meta: { sortField: 'marketing_status__c' },
    size: 150,
    cell: ({ row }) => row.original.marketing_status__c || '-',
  },
  {
    id: 'created_date',
    header: 'Created',
    accessorKey: 'created_date',
    meta: { sortField: 'created_date' },
    size: 120,
    cell: ({ row }) => (row.original.created_date ? formatDate(row.original.created_date) : '-'),
  },
  {
    id: 'actions',
    header: () => <div className="text-right">Actions</div>,
    size: 100,
    minSize: 80,
    maxSize: 150,
    enableResizing: false,
    cell: ({ row }) => {
      const fullName = `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim()
      return (
        <div className="text-right">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/contacts/${row.original.id}`} aria-label={`View contact ${fullName}`}>
              View
            </Link>
          </Button>
        </div>
      )
    },
  },
]

export const contactsDefaultColumnSizing: ColumnSizingState = {
  select: 50,
  avatar: 60,
  name: 200,
  title: 180,
  email: 220,
  phone: 180,
  account_name: 180,
  owner_name: 150,
  marketing_status__c: 150,
  created_date: 120,
  actions: 100,
}
